import { config } from 'config';
import type { Book, BookCreateInput, BookUpdateInput, ListBooksResponse, ExternalBook } from './types';

const base = config.apiBaseUrl;

function getJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    return res.json().then(
      (body: { message?: string; code?: string }) => {
        throw new Error(body.message ?? `HTTP ${res.status}`);
      },
      () => {
        throw new Error(`HTTP ${res.status}`);
      }
    );
  }
  if (res.status === 204) return Promise.resolve(undefined as T);
  return res.json();
}

/** Search our books (for autocomplete). */
export async function searchBooks(query: string, limit = 10): Promise<Book[]> {
  const res = await getBooks({ q: query || undefined, limit, page: 1 });
  return res.items;
}

/** Search external APIs when no local results. */
export async function externalSearchBooks(query: string): Promise<ExternalBook[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const search = new URLSearchParams();
  search.set('q', trimmed);
  const res = await fetch(`${base}/books/external-search?${search.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message =
      body && typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message?: unknown }).message ?? '')
        : '';
    throw new Error(
      message.trim() || (res.status === 400 ? 'Invalid query' : `HTTP ${res.status}`),
    );
  }
  return getJson<ExternalBook[]>(res);
}

export async function getBooks(params?: {
  page?: number;
  limit?: number;
  q?: string;
  placement?: string;
  readStatus?: string;
  seriesId?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<ListBooksResponse> {
  const search = new URLSearchParams();
  if (params?.page != null) search.set('page', String(params.page));
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.q) search.set('q', params.q);
  if (params?.placement) search.set('placement', params.placement);
  if (params?.readStatus) search.set('readStatus', params.readStatus);
  if (params?.seriesId) search.set('seriesId', params.seriesId);
  if (params?.sortBy) search.set('sortBy', params.sortBy);
  if (params?.sortOrder) search.set('sortOrder', params.sortOrder);
  const query = search.toString();
  const url = query ? `${base}/books?${query}` : `${base}/books`;
  const res = await fetch(url, { credentials: 'include' });
  return getJson<ListBooksResponse>(res);
}

export async function getBook(id: string): Promise<Book | null> {
  const res = await fetch(`${base}/books/${id}`, { credentials: 'include' });
  if (res.status === 404) return null;
  return getJson<Book>(res);
}

export async function createBook(data: BookCreateInput): Promise<Book> {
  const res = await fetch(`${base}/books`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return getJson<Book>(res);
}

export async function updateBook(id: string, data: BookUpdateInput): Promise<Book> {
  const res = await fetch(`${base}/books/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return getJson<Book>(res);
}

export async function deleteBook(id: string): Promise<void> {
  const res = await fetch(`${base}/books/${id}`, { method: 'DELETE', credentials: 'include' });
  await getJson<undefined>(res);
}
